import React, { useEffect, useState } from 'react';
import { Container, Typography, Card, CardContent, TextField, InputAdornment, IconButton, Button, Box, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import apiLogin from '../services/apiLogin';
import { useToast } from '../components/ToastContext';
import errorIcon from '../assets/error.png';
import logo from '../assets/nttlogoblanco1.png';
import { useAuth } from '../services/authContext';
import { loadConfig } from '../services/configLoader';
import { getSsoLoginUrl } from '../services/ssoUrl';
import { Message } from '../Utils/message';
import gainwellLogo from '../assets/gainwell_logo.svg';
import {
  clearAuthRedirectInProgress,
  markAuthRedirectInProgress,
} from '../Utils/common';
import { useConfig } from "../services/ConfigContext";
import { hasPermission } from '../Utils/role';

const Login = () => {
    sessionStorage.removeItem("activeAccountId");
    localStorage.removeItem("selectedProvider");
    localStorage.removeItem("selectedProviderId");
    localStorage.removeItem("selectedProjectId");
    localStorage.removeItem("selectedtenant");
    localStorage.removeItem("cmp.activeAccountSelection");
  const navigate = useNavigate();
  const toast = useToast();
  const theme = useTheme();
  const { configData } = useConfig();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ username: '', password: '', general: '' });

  const { isAuthenticated, authResolved, setIsAuthenticated } = useAuth();
  const [config, setConfig] = useState(null);
  const isGainwellLogo = configData?.isGainwellLogo || config?.isGainwellLogo;

  const getPostLoginRoute = () => {
    const rawRole = (sessionStorage.getItem('role') || sessionStorage.getItem('rolename') || '').toLowerCase().trim();
    const isSuperAdmin = hasPermission('IS_SUPER_ADMIN') || rawRole === 'l4';

    return isSuperAdmin ? '/audit' : '/dashboard';
  };

  useEffect(() => {
    sessionStorage.removeItem("activeAccountId");
    localStorage.removeItem("selectedProvider");
    localStorage.removeItem("selectedProviderId");
    localStorage.removeItem("selectedProjectId");
    localStorage.removeItem("selectedtenant");
    localStorage.removeItem("cmp.activeAccountSelection");
    clearAuthRedirectInProgress();

    async function load() {
      const cfg = await loadConfig();
      setConfig(cfg);
    }
    load();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("accessToken")) { return; }
    if (globalThis.tokenInterval) {
      clearInterval(globalThis.tokenInterval);
      globalThis.tokenInterval = null;
    }
  }, []);

  useEffect(() => {
    if (!authResolved) {
      return;
    }

    if (isAuthenticated) {
      sessionStorage.setItem("justLoggedIn", "true");
      navigate(getPostLoginRoute(), { replace: true });
    }
  }, [authResolved, isAuthenticated, navigate]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    if (event.target.value) {
      setErrors(prev => ({ ...prev, username: '' }));
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (event.target.value) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handlePortalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newErrors = {};
    if (!username) newErrors.username = 'Email is required';
    if (!password) newErrors.password = Message.pwReq;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const {
        userid,
        userRole,
        clientId,
        name,
        gender,
        clientName,
        emailId,
        organizationId,
        organizationName,
        token,
        sessionId,
        role,
        permissions, 
        menus       
      } = await apiLogin({ username, password });

      // Save user identity details
      sessionStorage.setItem('userId', userid);
      sessionStorage.setItem('rolename', userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase() : '');
      sessionStorage.setItem('role', role ? role.toLowerCase().trim() : (userRole || '').toLowerCase().trim());
      sessionStorage.setItem('username', name);
      sessionStorage.setItem('organizationId', organizationId);
      sessionStorage.setItem('organizationName', organizationName);
      sessionStorage.setItem('emailId', emailId);

      // Save permissions/menus if available
      if (permissions) {
        sessionStorage.setItem('userPermissions', JSON.stringify(permissions));
      }
      if (menus) {
        sessionStorage.setItem('userMenus', JSON.stringify(menus));
      }

      localStorage.setItem('clientid', clientId);
      localStorage.setItem('gender', gender);
      localStorage.setItem('clientName', clientName);
      localStorage.setItem('role', role ? role.toLowerCase().trim() : (userRole || '').toLowerCase().trim());

      // Save Auth Token & Session
      sessionStorage.setItem('accessToken', token);
      sessionStorage.setItem('sessionId', sessionId);

      // Prep state for the Account Selector popup
      sessionStorage.setItem("justLoggedIn", "true");
      sessionStorage.removeItem("activeAccountId");
      localStorage.removeItem("selectedProvider");
      localStorage.removeItem("selectedProjectId");

      setIsAuthenticated(true);
      toast.success('Login successful');

      navigate(getPostLoginRoute(), { replace: true });
    } catch (error) {
      setErrors(prev => ({ ...prev, general: error.message || 'Invalid Email or Password' }));
      toast.error(error.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    const token = sessionStorage.getItem('accessToken');

    if (token) {
      navigate(getPostLoginRoute(), { replace: true });
      return;
    }
    
    sessionStorage.removeItem("activeAccountId");
    sessionStorage.setItem("justLoggedIn", "true");
    markAuthRedirectInProgress();
    const ssoLoginUrl = getSsoLoginUrl(config);
    globalThis.location.href = ssoLoginUrl;
  };

  if (!config) {
    return null;
  }

  return (
    <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', bgcolor: theme.palette.background.sideBg, borderRadius: theme.shape.borderRadius }}>
      <Typography variant="h4" align="center" gutterBottom>
        <img src={logo} alt="Logo" style={{ height: 50 }} />
      </Typography>

      {/* Form Login (when SSO is disabled) */}
      {!config.isSSOEnabled && (
        <Card sx={{ width: '100%', maxWidth: 600, bgcolor: theme.palette.background.sideBg, mt: 2 }}>
          <CardContent sx={{ bgcolor: theme.palette.background.default }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              {errors.general && (
                <Box display="flex" alignItems="center" sx={{ marginBottom: 2 }}>
                  <img src={errorIcon} alt="Error Icon" style={{ height: 20, marginRight: 8 }} />
                  <Typography color="error">{errors.general}</Typography>
                </Box>
              )}
              <form onSubmit={handlePortalLogin}>
                <TextField 
                  label="Email" 
                  variant="outlined" 
                  fullWidth 
                  margin="normal" 
                  value={username} 
                  onChange={handleUsernameChange} 
                  autoComplete="off" 
                  error={Boolean(errors.username)} 
                  helperText={errors.username && (
                    <Box display="flex" alignItems="center">
                      <img src={errorIcon} alt="Error Icon" style={{ height: 20, marginRight: 8 }} />
                      {errors.username}
                    </Box>
                  )} 
                />

                <TextField
                  label="Password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowPassword}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  }}
                  autoComplete="new-password"
                  error={Boolean(errors.password)}
                  helperText={errors.password && (
                    <Box display="flex" alignItems="center">
                      <img src={errorIcon} alt="Error Icon" style={{ height: 20, marginRight: 8 }} />
                      {errors.password}
                    </Box>
                  )}
                />

                <Box sx={{ display: "flex", justifyContent: 'space-between', alignItems: 'center', width: '96%' }}>
                  <Button variant="contained" sx={{ marginTop: 2, backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }} type="submit" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : <Typography variant="button">SIGN IN</Typography>}
                  </Button>
                  <Typography variant="body1" sx={{ marginTop: 2, color: '#00bcd4', cursor: 'pointer' }}>
                    Forgot Password?
                  </Typography>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* SSO Login */}
      {config?.isSSOEnabled && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '100%', 
          maxWidth: 400, 
          mt: 4,
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(4),
          borderRadius: theme.shape.borderRadius,
        }}>
          {isGainwellLogo && ( 
            <Box sx={{ display: "grid", alignItems: 'center' }}>
              <img src={gainwellLogo} alt="Gainwell Logo" style={{ height: 40, marginBottom: 20 }} />
            </Box>
          )}
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ 
              marginTop: 3, 
              height: 50,
            }} 
            onClick={handleSSOLogin} 
          >
            Sign In with SSO
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default Login;